import React from "react";
import { Dialog, DialogContent, useMediaQuery, useTheme } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import CircleIcon from "@mui/icons-material/Circle";
import { useTranslation } from "react-i18next";

interface ICancellationPolicySheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const CancellationPolicySheet: React.FC<ICancellationPolicySheetProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation("webReceipt");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const PolicyContent = () => (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-base font-bold text-mineshaft-900">
          {t("cancelPolicyBottomSheet_title")}
        </h2>
      </div>

      {/* Timeline */}
      <div className="flex flex-col">
        {/* booking confirmed */}
        <div className="flex items-start space-x-4">
          <div className="flex flex-col items-center relative">
            <div className="flex flex-col items-center space-y-1">
              <div className="w-2 h-3 bg-transparent"></div>
              <div className="w-2 h-6 bg-green-300"></div>
              <div className="w-2 h-2 bg-green-300"></div>
              <div className="w-2 h-2 bg-green-300"></div>
              <div className="w-2 h-2 bg-green-300"></div>
            </div>
            <CheckCircleIcon
              sx={{
                fontSize: 24,
                color: "#22c55e",
                position: "absolute",
                top: -2,
                zIndex: 1,
              }}
            />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-mineshaft-900">
              {t("cancelPolicyBottomSheet_pointOne")}
            </h3>
            <p className="text-base font-normal leading-tight text-mineshaft-700">
              {t("cancelPolicyBottomSheet_pointOneDescription")}
            </p>
          </div>
        </div>
        {/* driver matched */}
        <div className="flex items-start space-x-4 mt-2">
          <div className="flex flex-col items-center relative">
            <div className="flex flex-col items-center space-y-1">
              <div className="w-2 h-5 bg-transparent"></div>
              <div className="w-2 h-16 bg-green-300"></div>
            </div>
            <PersonSearchIcon
              sx={{
                fontSize: 24,
                color: "#22c55e",
                position: "absolute",
                top: -2,
                zIndex: 1,
              }}
            />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-mineshaft-900">
              {t("cancelPolicyBottomSheet_pointTwo")}
            </h3>
            <p className="text-base font-normal leading-tight text-mineshaft-700">
              {t("cancelPolicyBottomSheet_pointTwoDescription")}
            </p>
          </div>
        </div>
        {/*  3 minutes after matching */}
        <div className="flex items-start space-x-4 mt-2">
          <div className="flex flex-col items-center relative">
            <div className="flex flex-col items-center space-y-1">
              <div className="w-2 h-5 bg-transparent"></div>
              <div
                className="w-2 h-16"
                style={{
                  background:
                    "linear-gradient(to bottom, #FFA500, transparent)",
                }}
              ></div>
            </div>
            <CircleIcon
              sx={{
                fontSize: 24,
                color: "#FFA500",
                position: "absolute",
                top: -2,
                zIndex: 1,
              }}
            />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-mineshaft-900">
              {t("cancelPolicyBottomSheet_pointThree")}
            </h3>
            <ul className="text-base font-normal leading-tight text-mineshaft-700 mt-1">
              {t("cancelPolicyBottomSheet_pointThreeDescription")
                .split("\n")
                .map((line, index) => (
                  <li
                    key={index}
                    className={`flex items-start ${index > 0 ? "mt-1" : ""}`}
                  >
                    <span className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    {line.replace(/^- /, "")}
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </div>
      <button
        onClick={onClose}
        className="w-full bg-blue-600 text-white py-3 rounded-full font-semibold text-base mt-8 hover:bg-blue-700 transition"
      >
        {t("cancelPolicyBottomSheet_cta")}
      </button>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-end justify-center">
            <div
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={onClose}
            />

            <div className="relative bg-white rounded-t-2xl max-h-[90vh] w-full p-6 overflow-y-auto animate-slide-up">
              <PolicyContent />
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: "rounded-2xl p-2",
      }}
    >
      <DialogContent className="p-6">
        <PolicyContent />
      </DialogContent>
    </Dialog>
  );
};

export default CancellationPolicySheet;
